import App from './App.vue';
import i18n from './i18n';
import { piniaPluginErrorNotifier, piniaPluginLoadingState } from './plugins';
import { setupAnalytics } from './plugins/analytics';
import { setupProductTour, productTour } from './plugins/productTour';
import { setupTelemetry } from './plugins/telemetry';
import { initializeRouterWithFeatureFlags } from './router/initializeRouterWithFeatureFlags';
import { ASSISTANT_SERVICE } from '@/@core/services/api/AssistantService';
import { FEATURE_CLIENT } from '@/@core/services/feature-flag/FeatureClient';
import { hashString } from '@/@core/utils';
import { getFaroApiKey } from '@/@core/utils/getFaroApiKey';
import { updateHtmlLangPlugin } from '@/plugins/updateHtmlLangPlugin';
import { useNotificationStore } from '@/stores/useNotificationStore';
import { useUserStore } from '@/stores/userStore';
import AADesignSystem from '@aleph-alpha/ds-components-vue';
import { BAGGAGE_KEYS, BaggageScope, telemetry } from '@aleph-alpha/lib-telemetry';
import '@unocss/reset/tailwind.css';
import { createPinia } from 'pinia';
import { createPersistedState } from 'pinia-plugin-persistedstate';
import 'virtual:uno.css';
import { createApp } from 'vue';

const pinia = createPinia();

export const app = createApp(App);

app.use(i18n);

// @ts-expect-error AADesignSystem is a plugin, but it's not typed as such in the package.
app.use(AADesignSystem);

pinia.use(
  createPersistedState({
    key: (id) => {
      const userStore = useUserStore();
      const prefix = userStore.userId ? userStore.userId : hashString('assistantuicache', true);
      return `${prefix}-${id}`;
    },
  })
);
pinia.use(piniaPluginLoadingState);
pinia.use(piniaPluginErrorNotifier(useNotificationStore, i18n.global));
app.use(pinia);
app.use(updateHtmlLangPlugin);

const featureFlags = await ASSISTANT_SERVICE.featureFlags();
FEATURE_CLIENT.setFlags(featureFlags);
FEATURE_CLIENT.setFeatureSet((await ASSISTANT_SERVICE.featureSet()).feature_set);
const router = await initializeRouterWithFeatureFlags(FEATURE_CLIENT);

app.use(router);
router.isReady().then(async () => {
  const faroApiKey = await getFaroApiKey();
  await setupTelemetry(app, faroApiKey);
  await setupAnalytics(app, faroApiKey);
  await setupProductTour(app, true);

  // Add environment tag to product tour
  productTour.addTags({
    environment: import.meta.env.MODE,
  });
});

const enabledFeatures = Object.entries(featureFlags)
  .filter(([, enabled]) => Boolean(enabled))
  .map(([featureName]) => featureName);

if (enabledFeatures.length > 0) {
  telemetry.setBaggage(BAGGAGE_KEYS.FEATURE_FLAGS, enabledFeatures.join(','), BaggageScope.SESSION);
}

app.mount('#app');
